/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { VehicleStatus, VehicleType } from 'src/graphql/generated/graphql.generated';
import {
  WaypointSummaryRecord,
  WaypointSummaryProps,
} from 'src/store/generated/waypoint-summary-record.generated';
import {
  PositionReportSummaryRecord,
  PositionReportSummaryProps,
} from 'src/store/generated/position-report-summary-record.generated';
import {
  CoordinatesRecord,
  CoordinatesProps,
} from 'src/store/generated/coordinates-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface VehicleSummaryProps {
  id: string;
  type: VehicleType;
  serialNumber: string | null;
  registrationNumber: string;
  dispatchCompanyId: string | null;
  from: WaypointSummaryProps | null;
  to: WaypointSummaryProps | null;
  unitSummaryId: string;
  makeName: string | null;
  modelName: string | null;
  regionId: string;
  status: VehicleStatus;
  statusDescription: string;
  etlo: Date | null;
  eta: Date | null;
  currentCardNumber: string | null;
  currentCardSummaryId: string | null;
  latestPositionReportSummary: PositionReportSummaryProps | null;
  lastQualifyingPositionReportTime: Date | null;
  lastKnownCoordinates: CoordinatesProps | null;
  currentOrNextLegSequence: number | null;
  totalLegCount: number | null;
  dateOnFinalMarked: Date | null;
  communicationCenterId: string | null;
  vehicleDispositionId: string;
  unitId: string;
  currentCardId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
}

export class VehicleSummaryRecord extends Record<VehicleSummaryProps>({
  id: null as any,
  type: null as any,
  serialNumber: null as any,
  registrationNumber: null as any,
  dispatchCompanyId: null as any,
  from: null as any,
  to: null as any,
  unitSummaryId: null as any,
  makeName: null as any,
  modelName: null as any,
  regionId: null as any,
  status: null as any,
  statusDescription: null as any,
  etlo: null as any,
  eta: null as any,
  currentCardNumber: null as any,
  currentCardSummaryId: null as any,
  latestPositionReportSummary: null as any,
  lastQualifyingPositionReportTime: null as any,
  lastKnownCoordinates: null as any,
  currentOrNextLegSequence: null as any,
  totalLegCount: null as any,
  dateOnFinalMarked: null as any,
  communicationCenterId: null as any,
  vehicleDispositionId: null as any,
  unitId: null as any,
  currentCardId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
}) {
  public static fromJS(json: {
    id: string;
    type: VehicleType;
    serialNumber: string | null;
    registrationNumber: string;
    dispatchCompanyId: string | null;
    from: any;
    to: any;
    unitSummaryId: string;
    makeName: string | null;
    modelName: string | null;
    regionId: string;
    status: VehicleStatus;
    statusDescription: string;
    etlo: string | number | Date | null;
    eta: string | number | Date | null;
    currentCardNumber: string | null;
    currentCardSummaryId: string | null;
    latestPositionReportSummary: any;
    lastQualifyingPositionReportTime: string | number | Date | null;
    lastKnownCoordinates: any;
    currentOrNextLegSequence: number | null;
    totalLegCount: number | null;
    dateOnFinalMarked: string | number | Date | null;
    communicationCenterId: string | null;
    vehicleDispositionId: string;
    unitId: string;
    currentCardId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
  }): VehicleSummaryRecord {
    if (json == null) {
      throw new Error('VehicleSummaryRecord.fromJS: parameter cannot be null');
    }
    return new VehicleSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'VehicleSummaryJson.id',
      ),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', VehicleType.Unspecified),
        'VehicleSummaryJson.type',
      ),
      serialNumber: json.serialNumber,
      registrationNumber: getFromOrThrow(
        json,
        (j) => j.registrationNumber,
        'VehicleSummaryJson.registrationNumber',
      ),
      dispatchCompanyId: json.dispatchCompanyId,
      from: json.from != null ? new WaypointSummaryRecord(json.from) : null,
      to: json.to != null ? new WaypointSummaryRecord(json.to) : null,
      unitSummaryId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitSummaryId', '00000000-0000-0000-0000-000000000000'),
        'VehicleSummaryJson.unitSummaryId',
      ),
      makeName: json.makeName,
      modelName: json.modelName,
      regionId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
        'VehicleSummaryJson.regionId',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', VehicleStatus.AtWaypoint),
        'VehicleSummaryJson.status',
      ),
      statusDescription: getFromOrThrow(
        json,
        (j) => j.statusDescription,
        'VehicleSummaryJson.statusDescription',
      ),
      etlo: json.etlo != null ? new Date(json.etlo) : null,
      eta: json.eta != null ? new Date(json.eta) : null,
      currentCardNumber: json.currentCardNumber,
      currentCardSummaryId: json.currentCardSummaryId,
      latestPositionReportSummary:
        json.latestPositionReportSummary != null
          ? new PositionReportSummaryRecord(json.latestPositionReportSummary)
          : null,
      lastQualifyingPositionReportTime:
        json.lastQualifyingPositionReportTime != null
          ? new Date(json.lastQualifyingPositionReportTime)
          : null,
      lastKnownCoordinates:
        json.lastKnownCoordinates != null ? new CoordinatesRecord(json.lastKnownCoordinates) : null,
      currentOrNextLegSequence: json.currentOrNextLegSequence,
      totalLegCount: json.totalLegCount,
      dateOnFinalMarked: json.dateOnFinalMarked != null ? new Date(json.dateOnFinalMarked) : null,
      communicationCenterId: json.communicationCenterId,
      vehicleDispositionId: getFromOrThrow(
        json,
        (j) =>
          propValueOrDefault(j, 'vehicleDispositionId', '00000000-0000-0000-0000-000000000000'),
        'VehicleSummaryJson.vehicleDispositionId',
      ),
      unitId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
        'VehicleSummaryJson.unitId',
      ),
      currentCardId: json.currentCardId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'VehicleSummaryJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    type: VehicleType;
    serialNumber: string | null;
    registrationNumber: string;
    dispatchCompanyId: string | null;
    from: any;
    to: any;
    unitSummaryId: string;
    makeName: string | null;
    modelName: string | null;
    regionId: string;
    status: VehicleStatus;
    statusDescription: string;
    etlo: string | number | Date | null;
    eta: string | number | Date | null;
    currentCardNumber: string | null;
    currentCardSummaryId: string | null;
    latestPositionReportSummary: any;
    lastQualifyingPositionReportTime: string | number | Date | null;
    lastKnownCoordinates: any;
    currentOrNextLegSequence: number | null;
    totalLegCount: number | null;
    dateOnFinalMarked: string | number | Date | null;
    communicationCenterId: string | null;
    vehicleDispositionId: string;
    unitId: string;
    currentCardId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
  }): [VehicleSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('VehicleSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new VehicleSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'VehicleSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', VehicleType.Unspecified),
            'VehicleSummaryJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      serialNumber: tryOrDefault(
        () => json.serialNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      registrationNumber: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => j.registrationNumber,
            'VehicleSummaryJson.registrationNumber',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dispatchCompanyId: tryOrDefault(
        () => json.dispatchCompanyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      from: tryOrDefault(
        () => (json.from != null ? new WaypointSummaryRecord(json.from) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      to: tryOrDefault(
        () => (json.to != null ? new WaypointSummaryRecord(json.to) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitSummaryId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitSummaryId', '00000000-0000-0000-0000-000000000000'),
            'VehicleSummaryJson.unitSummaryId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      makeName: tryOrDefault(
        () => json.makeName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      modelName: tryOrDefault(
        () => json.modelName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
            'VehicleSummaryJson.regionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', VehicleStatus.AtWaypoint),
            'VehicleSummaryJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      statusDescription: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => j.statusDescription, 'VehicleSummaryJson.statusDescription'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      etlo: tryOrDefault(
        () => (json.etlo != null ? new Date(json.etlo) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      eta: tryOrDefault(
        () => (json.eta != null ? new Date(json.eta) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentCardNumber: tryOrDefault(
        () => json.currentCardNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentCardSummaryId: tryOrDefault(
        () => json.currentCardSummaryId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestPositionReportSummary: tryOrDefault(
        () =>
          json.latestPositionReportSummary != null
            ? new PositionReportSummaryRecord(json.latestPositionReportSummary)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastQualifyingPositionReportTime: tryOrDefault(
        () =>
          json.lastQualifyingPositionReportTime != null
            ? new Date(json.lastQualifyingPositionReportTime)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastKnownCoordinates: tryOrDefault(
        () =>
          json.lastKnownCoordinates != null
            ? new CoordinatesRecord(json.lastKnownCoordinates)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentOrNextLegSequence: tryOrDefault(
        () => json.currentOrNextLegSequence,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      totalLegCount: tryOrDefault(
        () => json.totalLegCount,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateOnFinalMarked: tryOrDefault(
        () => (json.dateOnFinalMarked != null ? new Date(json.dateOnFinalMarked) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenterId: tryOrDefault(
        () => json.communicationCenterId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleDispositionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'vehicleDispositionId', '00000000-0000-0000-0000-000000000000'),
            'VehicleSummaryJson.vehicleDispositionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
            'VehicleSummaryJson.unitId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentCardId: tryOrDefault(
        () => json.currentCardId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'VehicleSummaryJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
    });

    return [record, errors];
  }

  constructor(props: VehicleSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('VehicleSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class VehicleSummaryViewModelBase implements VehicleSummaryProps {
  protected readonly _props: VehicleSummaryProps;
  private _json?: VehicleSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get type(): VehicleType {
    return this._props.type;
  }
  get serialNumber(): string | null {
    return this._props.serialNumber;
  }
  get registrationNumber(): string {
    return this._props.registrationNumber;
  }
  get dispatchCompanyId(): string | null {
    return this._props.dispatchCompanyId;
  }
  get from(): WaypointSummaryProps | null {
    return this._props.from;
  }
  get to(): WaypointSummaryProps | null {
    return this._props.to;
  }
  get unitSummaryId(): string {
    return this._props.unitSummaryId;
  }
  get makeName(): string | null {
    return this._props.makeName;
  }
  get modelName(): string | null {
    return this._props.modelName;
  }
  get regionId(): string {
    return this._props.regionId;
  }
  get status(): VehicleStatus {
    return this._props.status;
  }
  get statusDescription(): string {
    return this._props.statusDescription;
  }
  get etlo(): Date | null {
    return this._props.etlo;
  }
  get eta(): Date | null {
    return this._props.eta;
  }
  get currentCardNumber(): string | null {
    return this._props.currentCardNumber;
  }
  get currentCardSummaryId(): string | null {
    return this._props.currentCardSummaryId;
  }
  get latestPositionReportSummary(): PositionReportSummaryProps | null {
    return this._props.latestPositionReportSummary;
  }
  get lastQualifyingPositionReportTime(): Date | null {
    return this._props.lastQualifyingPositionReportTime;
  }
  get lastKnownCoordinates(): CoordinatesProps | null {
    return this._props.lastKnownCoordinates;
  }
  get currentOrNextLegSequence(): number | null {
    return this._props.currentOrNextLegSequence;
  }
  get totalLegCount(): number | null {
    return this._props.totalLegCount;
  }
  get dateOnFinalMarked(): Date | null {
    return this._props.dateOnFinalMarked;
  }
  get communicationCenterId(): string | null {
    return this._props.communicationCenterId;
  }
  get vehicleDispositionId(): string {
    return this._props.vehicleDispositionId;
  }
  get unitId(): string {
    return this._props.unitId;
  }
  get currentCardId(): string | null {
    return this._props.currentCardId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }

  public get isAir() {
    return this._props.type === VehicleType.Air;
  }
  public get isFixedWing() {
    return this._props.type === VehicleType.FixedWing;
  }
  public get isGround() {
    return this._props.type === VehicleType.Ground;
  }
  public get isRotorWing() {
    return this._props.type === VehicleType.RotorWing;
  }
  public get isUnspecified() {
    return this._props.type === VehicleType.Unspecified;
  }
  public get isAtWaypoint() {
    return this._props.status === VehicleStatus.AtWaypoint;
  }
  public get isEnRoute() {
    return this._props.status === VehicleStatus.EnRoute;
  }

  public isAnyType(...vehicleType: VehicleType[]) {
    return vehicleType.includes(this._props.type);
  }
  public isAnyStatus(...vehicleStatus: VehicleStatus[]) {
    return vehicleStatus.includes(this._props.status);
  }

  constructor(props: VehicleSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        type: this._props.type,
        serialNumber: this._props.serialNumber,
        registrationNumber: this._props.registrationNumber,
        dispatchCompanyId: this._props.dispatchCompanyId,
        from: this._props.from,
        to: this._props.to,
        unitSummaryId: this._props.unitSummaryId,
        makeName: this._props.makeName,
        modelName: this._props.modelName,
        regionId: this._props.regionId,
        status: this._props.status,
        statusDescription: this._props.statusDescription,
        etlo: this._props.etlo,
        eta: this._props.eta,
        currentCardNumber: this._props.currentCardNumber,
        currentCardSummaryId: this._props.currentCardSummaryId,
        latestPositionReportSummary: this._props.latestPositionReportSummary,
        lastQualifyingPositionReportTime: this._props.lastQualifyingPositionReportTime,
        lastKnownCoordinates: this._props.lastKnownCoordinates,
        currentOrNextLegSequence: this._props.currentOrNextLegSequence,
        totalLegCount: this._props.totalLegCount,
        dateOnFinalMarked: this._props.dateOnFinalMarked,
        communicationCenterId: this._props.communicationCenterId,
        vehicleDispositionId: this._props.vehicleDispositionId,
        unitId: this._props.unitId,
        currentCardId: this._props.currentCardId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllVehicleSummaryEntities = (state: AppState) =>
  state.entityStores.vehicleSummary.entities;

export const selectVehicleSummaryById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleSummary.entities.get(id);

export const selectVehicleSummaryMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleSummary.meta.get(id);

export const selectVehicleSummaryAndMetadataById = (id: string) =>
  createSelector(
    selectVehicleSummaryById(id),
    selectVehicleSummaryMetadataById(id),
    (vehicleSummary, meta) => ({ vehicleSummary, meta }),
  );

export function selectVehicleSummaryEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllVehicleSummaryEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, VehicleSummaryRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectVehicleSummaryEntitiesByType(type: VehicleType) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesBySerialNumber(serialNumber: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.serialNumber === serialNumber).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByRegistrationNumber(registrationNumber: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.registrationNumber === registrationNumber).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByDispatchCompanyId(dispatchCompanyId: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.dispatchCompanyId === dispatchCompanyId).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByUnitSummaryId(unitSummaryId: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.unitSummaryId === unitSummaryId).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByMakeName(makeName: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.makeName === makeName).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByModelName(modelName: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.modelName === modelName).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByStatus(status: VehicleStatus) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByStatusDescription(statusDescription: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.statusDescription === statusDescription).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByEtlo(etlo: Date) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.etlo === etlo).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByEta(eta: Date) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.eta === eta).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByCurrentCardNumber(currentCardNumber: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.currentCardNumber === currentCardNumber).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByCurrentCardSummaryId(currentCardSummaryId: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.currentCardSummaryId === currentCardSummaryId).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByLastQualifyingPositionReportTime(
  lastQualifyingPositionReportTime: Date,
) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities
      .filter((e) => e.lastQualifyingPositionReportTime === lastQualifyingPositionReportTime)
      .valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByCurrentOrNextLegSequence(
  currentOrNextLegSequence: number,
) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.currentOrNextLegSequence === currentOrNextLegSequence).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByTotalLegCount(totalLegCount: number) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.totalLegCount === totalLegCount).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByDateOnFinalMarked(dateOnFinalMarked: Date) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.dateOnFinalMarked === dateOnFinalMarked).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByCommunicationCenterId(communicationCenterId: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.communicationCenterId === communicationCenterId).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByVehicleDispositionId(vehicleDispositionId: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.vehicleDispositionId === vehicleDispositionId).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByCurrentCardId(currentCardId: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.currentCardId === currentCardId).valueSeq(),
  );
}

export function selectVehicleSummaryEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllVehicleSummaryEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
